import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import { CSSObject } from '../theming';
import { fallbackTheme } from '../theming/fallbackTheme';
import { FormElementsStatePropsLegacy, FormElementsColorProps } from './sharedTypes';
import { focusStyles } from '../utils';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

export const disabledColors = (theme: BabbelTheme): CSSObject => {
  const currentTheme = fallbackTheme(theme);

  return {
    background: currentTheme.color.disabled.surface,
    color: currentTheme.color.disabled.onSurfaceSubdued,
    additionalTextColor: currentTheme.color.disabled.onSurfaceSubdued,
    label: currentTheme.color.disabled.onSurfaceSubdued,
  };
};

export const stateColors = (
  theme: BabbelTheme,
  state: FormElementsStatePropsLegacy,
  color?: FormElementsColorProps
): CSSObject => {
  const currentTheme = fallbackTheme(theme);

  switch (state) {
    case 'default':
    case 'warning':
      return color === 'lightened'
        ? {
            background: currentTheme.color.neutral.surfaceLightened,
            color: currentTheme.color.accentInverse.onEnabled,
            additionalTextColor: currentTheme.color.neutral.onSurfaceSubdued1,
            label: currentTheme.color.neutral.onSurfaceSubdued1,
            border: currentTheme.color.lines.default,
          }
        : {
            background: currentTheme.color.neutral.surfaceDefault,
            color: currentTheme.color.neutral.onSurfaceDefault,
            additionalTextColor: currentTheme.color.neutral.onSurfaceSubdued1,
            label: currentTheme.color.neutral.onSurfaceSubdued1,
            border: currentTheme.color.lines.default,
          };
    case 'success':
      return {
        background: currentTheme.color.feedback.success.surface,
        color: currentTheme.color.feedback.success.onSurface,
        additionalTextColor: currentTheme.color.feedback.success.onSurface,
        label: currentTheme.color.neutral.onSurfaceSubdued1,
        border: currentTheme.color.feedback.success.onSurface,
      };
    case 'error':
      return {
        background: currentTheme.color.feedback.attention.surface,
        color: currentTheme.color.feedback.attention.onSurface,
        additionalTextColor: currentTheme.color.feedback.attention.onSurface,
        label: currentTheme.color.neutral.onSurfaceSubdued1,
        border: currentTheme.color.feedback.attention.onSurface,
      };
  }
};

export const readOnlyColors = (theme: BabbelTheme): CSSObject => {
  const currentTheme = fallbackTheme(theme);

  return {
    background: currentTheme.color.disabled.surface,
    color: currentTheme.color.neutral.onSurfaceSubdued1,
    additionalTextColor: currentTheme.color.neutral.onSurfaceSubdued1,
    label: currentTheme.color.neutral.onSurfaceSubdued1,
    arrowColor: currentTheme.color.disabled.onSurfaceSubdued,
  };
};

export const focusedColor = (theme: BabbelTheme, color: FormElementsColorProps) => {
  const currentTheme = fallbackTheme(theme);

  return color === 'lightened'
    ? currentTheme.color.accentInverse.enabled
    : currentTheme.color.accentInverse.enabledDarkened;
};

export const formElementsSharedStyles = (
  theme: BabbelTheme,
  state: FormElementsStatePropsLegacy,
  color: FormElementsColorProps,
  padding: string,
  height: string,
  disabled?: boolean
) => {
  const colorSet = stateColors(theme, state, color);
  const colorSetDisabled = disabledColors(theme);
  const colorSetReadOnly = readOnlyColors(theme);

  return {
    ...focusStyles({ theme, type: 'shadow' }),
    ':focus': {
      ...focusStyles({ theme, type: 'shadow' })[':focus'],
      background: state === 'default' ? focusedColor(theme, color) : colorSet.background,
    },
    '::placeholder': {
      color: state === 'default' ? fallbackTheme(theme).color.disabled.onSurfaceSubdued : 'inherit',
    },
    ':disabled': {
      color: colorSetDisabled.color,
      background: colorSetDisabled.background,
      cursor: 'not-allowed',
    },
    '&[aria-readonly="true"]': {
      background: colorSetReadOnly.background,
      color: colorSetReadOnly.color,
      boxShadow: 'none',
      pointerEvents: 'none' as React.CSSProperties['pointerEvents'],
      border: `1px solid ${colorSetReadOnly.background};}`,
    },
    ':read-only:focus': {
      ...focusStyles({ theme, type: 'shadow' })[':focus'],
      background: state === 'default' ? focusedColor(theme, color) : colorSet.background,
    },
    color: colorSet.color,
    background: colorSet.background,
    border: `1px solid ${disabled ? colorSetDisabled.background : colorSet.border};}`,
    borderRadius: tokens.radius.medium,
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding,
    height,
    outline: 0,
  };
};
